import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PlusCircle, Inbox, Pencil, PackageSearch, Search, X, Trash2, RotateCcw, Flame, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import CockpitLayout from "@/components/admin/CockpitLayout";
import StatusBadge from "@/components/admin/StatusBadge";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import LoadMore from "@/components/admin/LoadMore";
import { GoldAction, GoldLoading } from "@/components/admin/GoldUi";
import { api, fileUrl } from "@/lib/api";
import { useCursorList } from "@/lib/useCursorList";
import { STATUS_LABELS, STATUS_DOT, formatPrice, FALLBACK_IMG, CATEGORIES } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";

const TABS = ["all", "draft", "review", "available", "reserved", "sold", "hidden"];
const COUNTERS = ["draft", "review", "available", "reserved", "sold", "hidden"];

export default function Fundstuecke() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [category, setCategory] = useState("");
  const [confirm, setConfirm] = useState(null);

  const canPublish = can(user, "item:publish");
  const canDelete = can(user, "item:delete");
  const canRestore = can(user, "item:restore");
  const canPurge = can(user, "item:purge");
  const statusOptions = canPublish ? Object.keys(STATUS_LABELS) : ["draft", "review"];
  const isTrash = tab === "trash";

  const loadStats = useCallback(() => { api.get("/items/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  useEffect(() => { const id = setTimeout(() => setDebouncedQ(q), q ? 300 : 0); return () => clearTimeout(id); }, [q]);

  const listParams = useMemo(() => {
    if (tab === "trash") return { trash: true };
    const p = {};
    if (tab && tab !== "all") p.status = tab;
    if (debouncedQ) p.q = debouncedQ;
    if (category) p.category = category;
    return p;
  }, [tab, debouncedQ, category]);

  const { items, loading, loadingMore, error, hasMore, loadMore, reload } = useCursorList("/items", "items", listParams);

  useEffect(() => { loadStats(); }, [loadStats]);

  const refresh = () => { reload(); loadStats(); };

  const changeStatus = async (item, status) => {
    try {
      await api.post(`/items/${item.id}/status`, { status });
      toast.success(`Status: ${STATUS_LABELS[status]}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Status konnte nicht geändert werden."); }
  };

  const doDelete = (item) => setConfirm({
    title: "In den Papierkorb legen?",
    description: `„${item.title}" wird in den Papierkorb verschoben. Du kannst es 30 Tage lang wiederherstellen.`,
    confirmLabel: "In Papierkorb",
    danger: true,
    onConfirm: async () => {
      try {
        await api.delete(`/items/${item.id}`);
        refresh();
        toast.success("In den Papierkorb gelegt.", {
          action: canRestore ? { label: "Rückgängig", onClick: async () => { await api.post(`/items/${item.id}/restore`); refresh(); toast.success("Wiederhergestellt."); } } : undefined,
        });
      } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen."); }
    },
  });

  const doRestore = async (item) => {
    try { await api.post(`/items/${item.id}/restore`); toast.success("Wiederhergestellt."); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const doPurge = (item) => setConfirm({
    title: "Endgültig löschen?",
    description: `„${item.title}" wird unwiderruflich gelöscht. Das kann nicht rückgängig gemacht werden.`,
    confirmLabel: "Endgültig löschen",
    danger: true,
    onConfirm: async () => {
      try { await api.delete(`/items/${item.id}/purge`); toast.success("Endgültig gelöscht."); refresh(); }
      catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    },
  });

  return (
    <CockpitLayout active="fundstuecke" title="Fundstücke" inquiriesNew={stats.inquiries_new || 0}>
      <div className="fundio-page-stack" data-testid="gold-fundstuecke">
        <div className="fundio-section-head">
          <div>
            <h1 className="fundio-page-title" data-testid="fundstuecke-title">Fundstücke</h1>
            <p className="mt-1 fundio-muted">{stats.total || 0} Fundstücke gesamt</p>
          </div>
          <div className="flex gap-2">
            <GoldAction to="/dashboard/anfragen" data-testid="dashboard-inquiries-link" className="relative">
              <Inbox size={18} aria-hidden="true" /> Anfragen
              {stats.inquiries_new > 0 && <span className="fundio-count" style={{ fontSize: 14, color: "var(--f-gold-text)" }}>· {stats.inquiries_new}</span>}
            </GoldAction>
            <GoldAction to="/dashboard/neu" variant="gold" data-testid="dashboard-new-btn"><PlusCircle size={18} aria-hidden="true" /> Neues Fundstück</GoldAction>
          </div>
        </div>

        {/* status counters */}
        <section className="grid grid-cols-3 gap-3 sm:grid-cols-6" aria-label="Status-Übersicht">
          {COUNTERS.map((s) => (
            <button key={s} onClick={() => setTab(s)} data-testid={`stat-${s}`} aria-pressed={tab === s}
              className="fundio-panel text-left" style={{ padding: 16, borderColor: tab === s ? "var(--f-gold-text)" : undefined, boxShadow: tab === s ? "inset 0 -3px var(--f-gold)" : undefined }}>
              <span className="fundio-status-dot" style={{ background: STATUS_DOT[s], display: "block" }} />
              <p className="fundio-count" style={{ fontSize: 26, marginTop: 8 }}>{stats[s] ?? 0}</p>
              <p className="fundio-muted" style={{ fontSize: 13 }}>{STATUS_LABELS[s]}</p>
            </button>
          ))}
        </section>

        {/* search + category */}
        <div className="flex flex-wrap gap-3">
          <div className="relative min-w-0 flex-1">
            <Search size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 fundio-muted" aria-hidden="true" />
            <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen nach Titel, Beschreibung, Kategorie …"
              className="fundio-input" style={{ paddingLeft: 40, paddingRight: 40 }} />
            {q && <button onClick={() => setQ("")} data-testid="search-clear" aria-label="Suche leeren" className="absolute right-2 top-1/2 -translate-y-1/2 fundio-muted"><X size={16} /></button>}
          </div>
          <select data-testid="category-filter" value={category} onChange={(e) => setCategory(e.target.value)} className="fundio-input" style={{ maxWidth: 220 }}>
            <option value="">Alle Kategorien</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* tabs */}
        <div className="fundio-inquiry-filters" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(96px,1fr))" }}>
          {TABS.map((tt) => (
            <button key={tt} onClick={() => setTab(tt)} data-testid={`tab-${tt}`} aria-pressed={tab === tt}>
              {tt === "all" ? "Alle" : STATUS_LABELS[tt]}
            </button>
          ))}
          {(canDelete || canRestore) && (
            <button onClick={() => setTab("trash")} data-testid="tab-trash" aria-pressed={isTrash}
              style={isTrash ? { background: "#FFF6F3", color: "#903824", borderColor: "#B64832", boxShadow: "inset 0 -3px #B64832" } : undefined}>
              <Trash2 size={16} aria-hidden="true" /> Papierkorb
            </button>
          )}
        </div>

        {/* list */}
        {loading ? (
          <GoldLoading label="Fundstücke werden geladen …" />
        ) : items.length === 0 ? (
          <div className="fundio-panel fundio-state">
            <PackageSearch aria-hidden="true" />
            <strong>{isTrash ? "Papierkorb ist leer" : (q || category ? "Keine Treffer" : "Noch keine Fundstücke")}</strong>
            {!isTrash && !q && !category && <p>Lade ein Foto hoch und lass die KI den Rest machen.</p>}
            {!isTrash && !q && !category && <GoldAction to="/dashboard/neu" variant="gold"><PlusCircle size={18} aria-hidden="true" /> Erstes Fundstück</GoldAction>}
          </div>
        ) : (
          <div className="flex flex-col gap-3" data-testid="admin-item-list">
            {items.map((item) => (
              <div key={item.id} className="fundio-panel flex items-center gap-4" style={{ padding: 12 }}>
                <img src={fileUrl(item.thumb) || FALLBACK_IMG} alt="" loading="lazy" className="shrink-0 rounded-lg object-cover" style={{ width: 72, height: 72, boxShadow: "inset 0 0 0 1px var(--f-line)" }} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={item.status} />
                    <span className="fundio-muted" style={{ fontSize: 13 }}>{item.category}</span>
                    {item.location_name && <span data-testid={`item-location-${item.id}`} className="fundio-status" style={{ fontSize: 12 }}><MapPin size={12} aria-hidden="true" /> {item.location_name}</span>}
                  </div>
                  <h3 className="mt-1 truncate" style={{ fontWeight: 800, fontSize: 16 }}>{item.title}</h3>
                  <p className="fundio-muted" style={{ fontSize: 14 }}>{formatPrice(item.price)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {isTrash ? (
                    <>
                      {canRestore && <button onClick={() => doRestore(item)} data-testid={`restore-item-${item.id}`} className="fundio-action" title="Wiederherstellen"><RotateCcw size={18} aria-hidden="true" /><span className="hidden sm:inline">Wiederherstellen</span></button>}
                      {canPurge && <button onClick={() => doPurge(item)} data-testid={`purge-item-${item.id}`} title="Endgültig löschen" className="fundio-action" style={{ color: "#903824", borderColor: "#E7B9AF" }}><Flame size={18} aria-hidden="true" /></button>}
                    </>
                  ) : (
                    <>
                      <select value={statusOptions.includes(item.status) ? item.status : "review"} onChange={(e) => changeStatus(item, e.target.value)} data-testid={`status-select-${item.id}`}
                        disabled={!statusOptions.includes(item.status) && !canPublish}
                        className="fundio-input hidden sm:block" style={{ minHeight: 44, width: "auto", padding: "8px 10px", fontSize: 14 }}>
                        {statusOptions.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                      </select>
                      <Link to={`/dashboard/fundstuecke/${item.id}/bearbeiten`} data-testid={`edit-item-${item.id}`} aria-label="Bearbeiten"
                        className="fundio-action fundio-action--gold" style={{ minWidth: 44, padding: "10px 12px" }}><Pencil size={18} aria-hidden="true" /></Link>
                      {canDelete && <button onClick={() => doDelete(item)} data-testid={`delete-item-${item.id}`} title="In Papierkorb" className="fundio-action" style={{ color: "#903824", borderColor: "#E7B9AF", minWidth: 44 }}><Trash2 size={18} aria-hidden="true" /></button>}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <LoadMore hasMore={hasMore} loading={loadingMore} error={error} onLoad={loadMore} testid="items-load-more" />
      </div>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(v) => { if (!v) setConfirm(null); }}
        title={confirm?.title}
        description={confirm?.description}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        onConfirm={confirm?.onConfirm}
      />
    </CockpitLayout>
  );
}
